import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { FolderKanban, Layers, Table2, Wallet } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { PORTFOLIO_GROUPS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  MOCK_PORTFOLIO_GROUPS,
  formatCurrency,
  getPortfolioBatchRows,
} from "../../data/mock/dashboardData";
import { getInstitutionById, getInstitutionCaseFilterName } from "../../data/institutionsStore";
import {
  fetchInstitutionPortfolioGroups,
  normalizeOpsGroup,
} from "../../services/institutionWorkspaceApi";
import { navigateWithReturn } from "../../utils/navigationReturn";
import { showError } from "../../utils/toast";

export default function InstitutionPortfolioGroupsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const institution = getInstitutionById(id);
  const institutionName = institution ? getInstitutionCaseFilterName(institution) : "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        if (!useLiveApi) {
          const mock = getPortfolioBatchRows(MOCK_PORTFOLIO_GROUPS).filter(
            (row) =>
              row.provider_slug === id ||
              row.institution === institutionName ||
              row.institution === institution?.name
          );
          if (!cancelled) setRows(mock);
          return;
        }

        const groups = await fetchInstitutionPortfolioGroups(token, id);
        if (!cancelled) {
          setRows((groups || []).map((row) => normalizeOpsGroup(row, institution?.name)).filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          showError(error.message || "Could not load portfolio groups.");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, token, useLiveApi]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          groups: acc.groups + 1,
          accounts: acc.accounts + (row.accounts || 0),
          totalOwed: acc.totalOwed + (row.totalOwed || 0),
          outstanding: acc.outstanding + (row.outstanding || 0),
        }),
        { groups: 0, accounts: 0, totalOwed: 0, outstanding: 0 }
      ),
    [rows]
  );

  const columns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "name", header: "Portfolio group", type: "text" },
    { key: "uploadsCount", header: "Uploads", type: "number" },
    { key: "accounts", header: "Cases", type: "number" },
    { key: "totalOwed", header: "Total owed", type: "currency" },
    { key: "totalPaid", header: "Paid", type: "currency" },
    { key: "outstanding", header: "Outstanding", type: "currency" },
    { key: "lastUploadedAt", header: "Last upload", type: "date" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      headerClassName: "text-center",
      actions: (row) => [
        {
          key: "portfolio",
          label: "View portfolio data",
          description: "Uploaded balances for this group",
          icon: Table2,
          tone: "primary",
          onClick: () =>
            navigateWithReturn(
              navigate,
              `/dashboard/institutions/${id}/portfolio-groups/${row.id || row.numericId}`,
              location
            ),
        },
        {
          key: "cases",
          label: "View cases",
          description: "Unassigned cases in this group",
          icon: FolderKanban,
          tone: "sky",
          to: `/dashboard/cases/unassigned?group=${encodeURIComponent(row.id || row.numericId)}`,
        },
      ],
    },
  ];

  return (
    <PageShell
      title={institution?.name ? `${institution.name} portfolio groups` : "Portfolio groups"}
      description="Review the uploaded book, or open unassigned collection cases for a group."
      actions={<BackNavLink fallback={`/dashboard/institutions/${id}`} fallbackLabel="Institution" />}
    >
      {loading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading portfolio groups…
        </div>
      ) : null}

      <StatCardGrid cols={4}>
        <StatCard label="Groups" value={totals.groups} icon={Layers} tone="primary" />
        <StatCard label="Cases" value={totals.accounts} icon={FolderKanban} tone="sky" />
        <StatCard label="Total owed" value={formatCurrency(totals.totalOwed)} icon={Wallet} tone="accent" />
        <StatCard label="Outstanding" value={formatCurrency(totals.outstanding)} icon={Wallet} tone="emerald" />
      </StatCardGrid>

      <FilteredDataTable
        data={rows}
        columns={columns}
        config={{ ...PORTFOLIO_GROUPS_FILTER_CONFIG, title: "Group filters", metrics: [] }}
        pageSize={10}
        maxVisibleColumns={8}
        emptyMessage="No portfolio groups uploaded for this institution yet."
      />
    </PageShell>
  );
}
